import IconEmojiFormal from '../../../../shared/ui/icons/emodji/IconEmojiFormal';
import IconEmojiInformal from '../../../../shared/ui/icons/emodji/IconEmojiInformal';
import IconEmojiOptimistic from '../../../../shared/ui/icons/emodji/IconEmojiOptimistic';
import IconEmojiAssertive from '../../../../shared/ui/icons/emodji/IconEmojiAssertive';
import IconEmojiPlayful from '../../../../shared/ui/icons/emodji/IconEmojiPlayful';
import IconEmojiPersonal from '../../../../shared/ui/icons/emodji/IconEmojiPersonal';
import IconEmojiDefinitive from '../../../../shared/ui/icons/emodji/IconEmojiDefinitive';
import IconEmojiSarcastic from '../../../../shared/ui/icons/emodji/IconEmojiSarcastic';
import IconEmojiProfessional from '../../../../shared/ui/icons/emodji/IconEmojiProfessional';
import IconEmojiInspiring from '../../../../shared/ui/icons/emodji/IconEmojiInspiring';
import IconEmojiEmotional from '../../../../shared/ui/icons/emodji/IconEmojiEmotional';
import IconEmojiNeutral from '../../../../shared/ui/icons/emodji/IconEmojiNeutral';
import IconEmojiPersuasive from '../../../../shared/ui/icons/emodji/IconEmojiPersuasive';
import IconEmojiWitty from '../../../../shared/ui/icons/emodji/IconEmojiWitty';
import IconEmojiSerious from '../../../../shared/ui/icons/emodji/IconEmojiSerious';
import IconEmojiFun from '../../../../shared/ui/icons/emodji/IconEmojiFun';
import IconEmojiFriendly from '../../../../shared/ui/icons/emodji/IconEmojiFriendly';
import IconEmojiExciting from '../../../../shared/ui/icons/emodji/IconEmojiExciting';

interface Tone {
    text: string;
    tag: string;
    icon: JSX.Element;
    sliderLabels: {
        negative: string;
        positive: string;
    };
}

interface VoiceOption {
    text: string;
    tag: string;
    color: string;
    tooltip: JSX.Element;
}

export const tones: Tone[] = [
    {
        text: 'Friendly',
        tag: 'friendly',
        icon: <IconEmojiFriendly />,
        sliderLabels: { negative: 'hostile', positive: 'friendly' },
    },
    {
        text: 'Exciting',
        tag: 'exciting',
        icon: <IconEmojiExciting />,
        sliderLabels: { negative: 'dull', positive: 'exciting' },
    },
    {
        text: 'Fun',
        tag: 'fun',
        icon: <IconEmojiFun />,
        sliderLabels: { negative: 'tedious', positive: 'fun' },
    },
    {
        text: 'Serious',
        tag: 'serious',
        icon: <IconEmojiSerious />,
        sliderLabels: { negative: 'lighthearted', positive: 'serious' },
    },
    {
        text: 'Witty',
        tag: 'witty',
        icon: <IconEmojiWitty />,
        sliderLabels: { negative: 'unimaginative', positive: 'witty' },
    },
    {
        text: 'Persuasive',
        tag: 'persuasive',
        icon: <IconEmojiPersuasive />,
        sliderLabels: { negative: 'ineffective', positive: 'persuasive' },
    },
    {
        text: 'Neutral',
        tag: 'neutral',
        icon: <IconEmojiNeutral />,
        sliderLabels: { negative: 'biased', positive: 'neutral' },
    },
    {
        text: 'Emotional',
        tag: 'emotional',
        icon: <IconEmojiEmotional />,
        sliderLabels: { negative: 'stoic', positive: 'emotional' },
    },
    {
        text: 'Formal',
        tag: 'formal',
        icon: <IconEmojiFormal />,
        sliderLabels: { negative: 'casual', positive: 'formal' },
    },
    {
        text: 'Inspiring',
        tag: 'inspiring',
        icon: <IconEmojiInspiring />,
        sliderLabels: { negative: 'discouraging', positive: 'inspiring' },
    },
    {
        text: 'Professional',
        tag: 'professional',
        icon: <IconEmojiProfessional />,
        sliderLabels: { negative: 'amateurish', positive: 'professional' },
    },
    {
        text: 'Sarcastic',
        tag: 'sarcastic',
        icon: <IconEmojiSarcastic />,
        sliderLabels: { negative: 'straightforward', positive: 'sarcastic' },
    },
    {
        text: 'Personal',
        tag: 'personal',
        icon: <IconEmojiPersonal />,
        sliderLabels: { negative: 'detached', positive: 'personal' },
    },
    {
        text: 'Authoritative',
        tag: 'authoritative',
        icon: <IconEmojiDefinitive />,
        sliderLabels: { negative: 'ambiguous', positive: 'authoritative' },
    },
    {
        text: 'Playful',
        tag: 'playful',
        icon: <IconEmojiPlayful />,
        sliderLabels: { negative: 'stern', positive: 'playful' },
    },
    {
        text: 'Assertive',
        tag: 'assertive',
        icon: <IconEmojiAssertive />,
        sliderLabels: { negative: 'submissive', positive: 'assertive' },
    },
    {
        text: 'Optimistic',
        tag: 'optimistic',
        icon: <IconEmojiOptimistic />,
        sliderLabels: { negative: 'pessimistic', positive: 'optimistic' },
    },
    {
        text: 'Informal',
        tag: 'informal',
        icon: <IconEmojiInformal />,
        sliderLabels: { negative: 'formal', positive: 'informal' },
    },
];

export const voiceOptions: VoiceOption[] = [
    {
        text: 'Hustle',
        tag: 'hustle',
        color: '#f66731',
        tooltip: (
            <p>
                A voice that emphasizes hard work, ambition, and relentless drive. It’s energetic,
                no-nonsense, and encourages a go-getter mentality.
            </p>
        ),
    },
    {
        text: 'Glow-up',
        tag: 'glow-up',
        color: '#9ef344',
        tooltip: (
            <p>
                A tone that focuses on transformation, self-improvement, and positive change. It’s
                inspirational, empowering, and celebrates personal or brand evolution.
            </p>
        ),
    },
    {
        text: 'Buddy',
        tag: 'buddy',
        color: '#40E0D0',
        tooltip: (
            <p>
                A friendly, approachable, and casual voice that feels like chatting with a close
                friend. It’s warm, supportive, and easygoing.
            </p>
        ),
    },
    {
        text: 'Jester',
        tag: 'jester',
        color: '#FF6347',
        tooltip: (
            <p>
                A playful, witty, and humorous tone that doesn’t take itself too seriously. It’s all
                about entertaining and bringing joy.
            </p>
        ),
    },
    {
        text: 'Sparks',
        tag: 'sparks',
        color: '#ADFF2F',
        tooltip: (
            <p>
                A voice that ignites creativity, excitement, and fresh ideas. It’s energetic,
                inspiring, and encourages new possibilities.
            </p>
        ),
    },
    {
        text: 'Dreams',
        tag: 'dreams',
        color: '#87CEEB',
        tooltip: (
            <p>
                A tone that is aspirational, hopeful, and imaginative. It emphasizes big visions,
                possibilities, and achieving the impossible.
            </p>
        ),
    },
    {
        text: 'Hype',
        tag: 'hype',
        color: '#FF69B4',
        tooltip: (
            <p>
                An excitable, bold, and enthusiastic voice designed to build anticipation and buzz
                around people, products, or events.
            </p>
        ),
    },
    {
        text: 'Pride',
        tag: 'pride',
        color: '#b05fff',
        tooltip: (
            <p>
                A confident, bold, and celebratory tone that highlights identity, achievements, and
                a sense of belonging.
            </p>
        ),
    },
    {
        text: 'Muse',
        tag: 'muse',
        color: '#9370DB',
        tooltip: (
            <p>
                A thoughtful, inspiring, and creative voice that encourages imagination and sparks
                new ideas.
            </p>
        ),
    },
    {
        text: 'Quirk',
        tag: 'quirk',
        color: '#00FA9A',
        tooltip: (
            <p>
                A fun, offbeat, and playfully unconventional tone that celebrates uniqueness and
                stands out from the crowd.
            </p>
        ),
    },
    {
        text: 'Visionary',
        tag: 'visionary',
        color: '#8A2BE2',
        tooltip: (
            <p>
                A bold, future-focused, and inspirational voice that emphasizes innovation, big
                ideas, and long-term impact.
            </p>
        ),
    },
    {
        text: 'Support',
        tag: 'support',
        color: '#FFDAB9',
        tooltip: (
            <p>
                A caring, helpful, and reassuring voice that prioritizes guidance, customer care,
                and reliability.
            </p>
        ),
    },
    {
        text: 'Pro-tip',
        tag: 'pro-tip',
        color: '#5dabf6',
        tooltip: (
            <p>
                A practical, knowledgeable, and expert voice that delivers actionable advice and
                useful insights.
            </p>
        ),
    },
    {
        text: 'Solid',
        tag: 'solid',
        color: '#f4aff1',
        tooltip: (
            <p>
                A reliable, trustworthy, and straightforward tone that emphasizes stability,
                strength, and consistency.
            </p>
        ),
    },
    {
        text: 'Maverick',
        tag: 'maverick',
        color: '#DC143C',
        tooltip: (
            <p>
                A bold, rebellious, and unconventional voice that challenges norms and promotes
                independent thinking.
            </p>
        ),
    },
    {
        text: 'Mentor',
        tag: 'mentor',
        color: '#20B2AA',
        tooltip: (
            <p>
                A wise, nurturing, and experienced voice that offers guidance, personal development,
                and supportive wisdom.
            </p>
        ),
    },
    {
        text: 'Sage',
        tag: 'sage',
        color: '#2E8B57',
        tooltip: (
            <p>
                A calm, authoritative, and timeless voice that conveys deep knowledge and profound
                insights.
            </p>
        ),
    },
    {
        text: 'Dynamo',
        tag: 'dynamo',
        color: '#1E90FF',
        tooltip: (
            <p>
                An energetic, bold, and unstoppable tone that emphasizes action, momentum, and
                continuous progress.
            </p>
        ),
    },
];
